import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { flatten } from 'lodash';
import { JsonObject } from '../helpers';
import Environment from '../environment';
import { AuthProvider } from './auth.service';
import {
  ApiResponse,
  CreateErrorHandler,
  CreateResponse,
  DeleteResponse,
  handleCreateErrorResponse,
  handleCreateResponse,
  handleDeleteErrorResponse,
  handleDeleteResponse,
  handlePatchErrorResponse,
  handlePatchResponse,
  handlePostResponse,
  handleResponse,
  handlePostErrorResponse,
  handleUpdateErrorResponse,
  handleUpdateResponse,
  PatchErrorHandler,
  PatchResponse,
  UpdateErrorHandler,
  UpdateResponse,
  PostErrorHandler,
  ListResponse,
} from '.';

export const axiosClient = axios.create({
  baseURL: Environment.SP_API_URL,
  timeout: 10000,
  withCredentials: true,
  responseType: 'json',
  headers: {
    'X-Requested-By': 'surepath',
  },
});

axiosClient.interceptors.request.use((config) => {
  if (AuthProvider.token) {
    config.headers['Surepath-Authorization'] = `Bearer ${AuthProvider.token}`;
  }
  return config;
});

export const post = async <T = JsonObject>(
  path: string,
  data: JsonObject,
  onError: PostErrorHandler = handlePostErrorResponse
): Promise<T | null> => {
  return axiosClient
    .post(path, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response) => handlePostResponse<T>(response))
    .catch((error) => onError<T>(error as AxiosError));
};

export const create = async (
  path: string,
  data: JsonObject,
  onError: CreateErrorHandler = handleCreateErrorResponse
): Promise<CreateResponse> => {
  return axiosClient
    .post(path, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(handleCreateResponse)
    .catch(onError);
};

export const update = async (
  path: string,
  data: JsonObject | FormData,
  opts: AxiosRequestConfig = {},
  onError: UpdateErrorHandler = handleUpdateErrorResponse
): Promise<UpdateResponse> => {
  return axiosClient
    .put(path, data, {
      headers: {
        'Content-Type': 'application/json',
      },
      ...opts,
    })
    .then(handleUpdateResponse)
    .catch(onError);
};

export const patch = async (
  path: string,
  data: JsonObject,
  onError: PatchErrorHandler = handlePatchErrorResponse
): Promise<PatchResponse> => {
  return axiosClient
    .patch(path, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(handlePatchResponse)
    .catch(onError);
};

// unfortunately delete is a keyword
export const deleteApi = async (path: string, params?: JsonObject): Promise<DeleteResponse> => {
  return axiosClient
    .delete(path, { params })
    .then(handleDeleteResponse)
    .catch(handleDeleteErrorResponse);
};

export const get = async (
  path: string,
  params?: JsonObject | URLSearchParams
): Promise<ApiResponse> => {
  return axiosClient.get(path, { params }).then(handleResponse);
};

export const getAllPaged = async (path: string, pageSize: number): Promise<JsonObject[]> => {
  const responses: JsonObject[][] = [];
  let page = 1;
  let hasNext = true;

  do {
    const response = (await get(path, {
      page,
      pageSize,
    })) as ListResponse;
    hasNext = response.hasNext;
    responses.push(response.data);
    page += 1;
  } while (hasNext);

  return flatten(responses);
};

export const getBinaryFile = async (
  path: string,
  params?: JsonObject | URLSearchParams
): Promise<Blob> => {
  try {
    // Configure axios to receive binary data as response
    const response = await axiosClient.get(path, {
      params,
      responseType: 'arraybuffer', // This is the key setting
      headers: {
        Accept: 'application/zip, application/octet-stream', // Tell the server we want binary
      },
    });

    // Create a blob from the binary data
    const contentType = response.headers['content-type'] as string;
    return new Blob([response.data], {
      type: contentType,
    });
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};
